import React, { useCallback, useMemo } from 'react';
import { Tooltip } from '@primer/react';

import byteSize from 'byte-size';

import AlternatingRowDataTable from '../../../common/DataTable/AlternatingRowDataTable';
import Button from '../../../common/Button/Button';
import ClipboardLabel from '../../../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../../../common/Labels/CodeFormatLabel';
import Icon from '../../../common/icons/Icon';
import Label from '../../../common/Labels/Label';

import TrashFillIcon from '../../../common/icons/TrashFillIcon';
import VersionIcon from '../../../common/icons/VersionIcon';

import { formatDateWithTimeSeparator } from '../../../../utils/date_utils';
import { getThemeColor } from '../../../../utils/theme-utils';

const transformModelsToTableRows = (models = []) => models?.map((modelObj) => {
  const {
    model_versions: versions,
    workspace,
    model: {
      id, name, created_at, updated_at, owner_id, workspace_id
    },
  } = modelObj;

  const firstVersion = versions?.length > 0 ? versions[0]?.model_version : null;

  const file_hash = firstVersion ? firstVersion?.file_info?.sha : null;

  const shaSet = [];
  const bytes = versions.reduce((acc, version) => {
    const versionSize = version?.model_version?.file_info?.size;
    const versionSha = version?.model_version?.file_info?.sha;

    if (shaSet.includes(versionSha)) {
      return acc;
    } else {
      shaSet.push(versionSha);
      return versionSize + acc;
    }
  }, 0);

  return ({
    id,
    name,
    bytes,
    file_hash,
    owner_id,
    versions,
    latestVersion: firstVersion,
    updated_by: firstVersion?.created_by,
    workspace_name: workspace.name,
    workspace: {
      workspace_id,
      name: workspace.name,
      created_by: workspace.created_by,
    },
    created_at,
    updated_at,
  })
})

const truncateText = (inputText, truncateAt = 8) => {
  if (inputText.length < truncateAt + 1) {
    return inputText;
  }
  return `${inputText.substring(0, truncateAt)}...`;
}

const CellSubLabel = ({ children, ...rest }) => (
  <Label style={{ color: getThemeColor('info') }} {...rest} >{ children }</Label>
)

const NameCell = ({ value }) => {
  const truncatedName = truncateText(value || '--', 25);

  return (
    <div className="flex flex-col p-2">
      <Label className="break-words">
        {truncatedName}
      </Label>
    </div>
  )
}

const WorkspaceCell = ({ value, row }) => {
  const workspace = row?.original?.workspace;
  const truncatedName = truncateText(value || '--', 25);
  const owner = workspace?.created_by;
  const truncatedOwner = truncateText(workspace?.created_by || '--', 25);
  return (
    <div className="flex flex-col">
      <Label className="break-words">{truncatedName}</Label>
      {
        owner?.length > 0 && (
          <CellSubLabel className="mt-1 break-words">{truncatedOwner}</CellSubLabel>
        )
      }
    </div>
  )
}

const FileHashCell = ({ value }) => {
  const truncatedHash = truncateText(value || '--', 15);
  return (
    <div className="flex">
      <ClipboardLabel
        iconSize={16}
        labelValue={value}
        displayLabel={truncatedHash}
        LabelComponent={CodeFormatLabel}
        showClipboardIconOnHover
      />
    </div>
  )
}

// Storage Calculation/Units
const StorageSizeCell = ({ value }) => {
  const storage = value || 0;
  const storageData = byteSize(storage);
  const storageLabel = `${storageData.value} ${storageData.unit}`;
  return (
    <div className="flex flex-col">
      <Label>{value ? storageLabel : '--'}</Label>
    </div>
  )
}

const CreatedCell = ({ value }) => {
  if (value === null) {
    return <Label>--</Label>
  }
  return (
    <Label>{formatDateWithTimeSeparator(new Date(value).toISOString())}</Label>
  )
}

const UpdatedAtCell = ({ value, row }) => {
  if (value === null) {
    return <Label>--</Label>
  }

  const updatedBy = row?.original?.updated_by;

  return (
    <div className="flex flex-col">
      <Label>{formatDateWithTimeSeparator(new Date(value).toISOString())}</Label>
      {
        updatedBy && (
          <CellSubLabel className="mt-1 break-words">{updatedBy}</CellSubLabel>
        )
      }
    </div>
  )
}

const ActionCellRenderer = ({
  row, meta = {},
}) => {
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  }
  const onFirstButtonClick = useCallback((e) => {
    meta?.viewModelHistory(row);
    e.stopPropagation()
  }, [meta, row])

  const onSecondButtonClick = useCallback((e) => {
    meta?.removeModel(row)
    e.stopPropagation()
  }, [meta, row])

  const isActive = row?.original?.latestVersion?.deployed;

  return (
    <div className="flex flex-row justify-between items-center w-16">
      <Tooltip aria-label="Manage model versions">
        <Button sx={buttonStyle} onClick={onFirstButtonClick} >
          <Icon
            icon={VersionIcon}
            fill={getThemeColor('brand')}
            size={24}
            useTailwindSizing
          />
        </Button>
      </Tooltip>
      <Tooltip direction="nw" aria-label={isActive ? 'Unable to delete deployed model' : 'Delete model'}>
        <Button sx={buttonStyle} onClick={onSecondButtonClick} disabled={isActive}>
          <Icon
            icon={TrashFillIcon}
            width={24}
            height={20}
            fill={getThemeColor(isActive ? 'info' : 'brand')}
          />
        </Button>
      </Tooltip>
    </div>
  )
}

export const ModelAdminTable = ({
  models = [],
  removeModel = () => {},
  viewModelHistory = () => {},
  onModelClick = () => {},
  selectedPage = 1,
  resultsPerPage = 25,
}) => {
  const columns = useMemo(() => [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: NameCell,
      width: 200
    },
    {
      Header: 'Workspace',
      accessor: 'workspace_name',
      Cell: WorkspaceCell,
      width: 180
    },
    {
      Header: 'Created',
      accessor: 'created_at',
      Cell: CreatedCell,
      width: 120,
    },
    {
      Header: 'Last Updated',
      accessor: 'updated_at',
      id: 'Updated',
      Cell: UpdatedAtCell,
      width: 120,
    },
    {
      Header: 'File Hash',
      accessor: 'file_hash',
      Cell: FileHashCell,
      width: 120,
      tooltipText: 'Identifier for a stored artifact that is shared when the artifact is identical.',
    },
    {
      Header: 'Storage Size',
      accessor: 'bytes',
      Cell: StorageSizeCell,
      width: 110,
    },
    {
      Header: 'Actions',
      Cell: ActionCellRenderer,
      width: 80,
      disableSortBy: true,
    }
  ], []);

  const defaultSort = {
    id: 'name',
    desc: false
  }

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )
  const meta = React.useMemo(() => ({
    viewModelHistory, removeModel,
  }), [removeModel, viewModelHistory])

  const tableRows = transformModelsToTableRows(models);

  return (
    <AlternatingRowDataTable
      id="model-admin-table"
      columns={columns}
      data={tableRows}
      defaultColumn={defaultColumn}
      defaultSort={defaultSort}
      meta={meta}
      onItemClick={onModelClick}
      height={640}
      pagination={{
        resultsPerPage,
        selectedPage
      }}
    />
  )
};

export default ModelAdminTable;
